import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Truck, Bike, Store, Save, X, Plus } from "lucide-react";
import api from "@/lib/api";
import usePageTitle from "@/hooks/use-page-title";

/**
 * ShippingSettings — admin page for managing storefront shipping options:
 *   • Courier fee (₹)
 *   • Free-shipping threshold (₹)
 *   • Local-agent cities (Rapido/Porter delivery is available for)
 *   • Enable/disable individual methods
 * Persisted to `settings.shipping_config` on the backend.
 */
export default function ShippingSettings() {
  usePageTitle("Shipping Settings");
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newCity, setNewCity] = useState("");

  const load = async () => {
    try {
      const r = await api.get("/shipping/config");
      setCfg(r.data);
    } catch { toast.error("Load failed"); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!cfg) return;
    setBusy(true);
    try {
      const r = await api.put("/admin/shipping/config", {
        courier_fee: cfg.courier_fee,
        free_shipping_threshold: cfg.free_shipping_threshold,
        local_agent_cities: cfg.local_agent_cities,
        enable_pickup: cfg.enable_pickup,
        enable_local_agent: cfg.enable_local_agent,
        enable_courier: cfg.enable_courier,
      });
      setCfg(r.data);
      toast.success("Shipping settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const addCity = () => {
    const c = (newCity || "").trim();
    if (!c) return;
    if ((cfg.local_agent_cities || []).map((x) => x.toLowerCase()).includes(c.toLowerCase())) {
      toast.error(`${c} is already in the list`);
      return;
    }
    setCfg({ ...cfg, local_agent_cities: [...(cfg.local_agent_cities || []), c] });
    setNewCity("");
  };
  const removeCity = (c) => {
    setCfg({ ...cfg, local_agent_cities: (cfg.local_agent_cities || []).filter((x) => x !== c) });
  };

  if (!cfg) return <div className="p-6 text-sm text-zinc-500">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="shipping-settings-root">
      <div className="flex items-end justify-between border-b border-[hsl(var(--border))] pb-6">
        <div>
          <div className="overline mb-2">Storefront</div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter">Shipping Settings</h1>
          <p className="mt-2 text-sm text-zinc-500 max-w-2xl">Manage the delivery options a customer sees at checkout and the fee for each.</p>
        </div>
        <button
          onClick={save}
          disabled={busy}
          className="btn-swiss btn-accent disabled:opacity-50"
          data-testid="shipping-save-btn"
        >
          <Save size={14} /> {busy ? "Saving…" : "Save Changes"}
        </button>
      </div>

      {/* Fees */}
      <section className="card-swiss p-6 space-y-4">
        <div>
          <div className="overline text-[#FF6B00]">Courier</div>
          <h2 className="font-display text-xl font-black tracking-tight">Delivery Fee & Free-shipping Threshold</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs uppercase tracking-wider font-bold text-zinc-500 mb-1">Courier Fee (₹)</label>
            <input
              type="number"
              min="0"
              step="1"
              value={cfg.courier_fee}
              onChange={(e) => setCfg({ ...cfg, courier_fee: Math.max(0, parseFloat(e.target.value) || 0) })}
              className="w-full border-2 border-zinc-200 focus:border-[#FF6B00] rounded-xl px-3 py-2 text-sm outline-none"
              data-testid="courier-fee-input"
            />
            <p className="text-[11px] text-zinc-500 mt-1">Charged when customer picks "Delivery by Courier" and the subtotal is below the threshold.</p>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider font-bold text-zinc-500 mb-1">Free-shipping Threshold (₹)</label>
            <input
              type="number"
              min="0"
              step="10"
              value={cfg.free_shipping_threshold}
              onChange={(e) => setCfg({ ...cfg, free_shipping_threshold: Math.max(0, parseFloat(e.target.value) || 0) })}
              className="w-full border-2 border-zinc-200 focus:border-[#FF6B00] rounded-xl px-3 py-2 text-sm outline-none"
              data-testid="free-threshold-input"
            />
            <p className="text-[11px] text-zinc-500 mt-1">Set to 0 to disable free courier. Applies only to courier — pickup is always free.</p>
          </div>
        </div>
      </section>

      {/* Method toggles */}
      <section className="card-swiss p-6 space-y-4">
        <div>
          <div className="overline text-[#FF6B00]">Enabled Methods</div>
          <h2 className="font-display text-xl font-black tracking-tight">Show these options at Checkout</h2>
        </div>
        <div className="space-y-2">
          <ToggleRow
            icon={Store}
            title="Pickup from Shop"
            desc="Customer collects from your Navi Mumbai shop — no delivery fee."
            checked={!!cfg.enable_pickup}
            onToggle={() => setCfg({ ...cfg, enable_pickup: !cfg.enable_pickup })}
            testid="enable-pickup"
          />
          <ToggleRow
            icon={Bike}
            title="Delivery by Rapido / Porter"
            desc="Customer pays the delivery agent directly on drop-off — nothing added to your invoice. Available for the local cities below only."
            checked={!!cfg.enable_local_agent}
            onToggle={() => setCfg({ ...cfg, enable_local_agent: !cfg.enable_local_agent })}
            testid="enable-local-agent"
          />
          <ToggleRow
            icon={Truck}
            title="Delivery by Courier"
            desc="Standard courier — fee applied to invoice, free above the threshold you set above."
            checked={!!cfg.enable_courier}
            onToggle={() => setCfg({ ...cfg, enable_courier: !cfg.enable_courier })}
            testid="enable-courier"
          />
        </div>
      </section>

      {/* Local-agent cities */}
      <section className="card-swiss p-6 space-y-4">
        <div>
          <div className="overline text-[#FF6B00]">Local Delivery</div>
          <h2 className="font-display text-xl font-black tracking-tight">Cities Eligible for Rapido / Porter</h2>
          <p className="text-sm text-zinc-500 mt-1">The "Delivery by Rapido / Porter" option only appears when the customer's address city matches one of these.</p>
        </div>
        <div className="flex gap-2">
          <input
            value={newCity}
            onChange={(e) => setNewCity(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCity())}
            placeholder="+ Add a city (e.g. Kalyan)"
            className="flex-1 border-2 border-zinc-200 focus:border-[#FF6B00] rounded-full px-3 py-2 text-sm outline-none"
            data-testid="new-city-input"
          />
          <button
            onClick={addCity}
            disabled={!newCity.trim()}
            className="inline-flex items-center gap-1 bg-[#FF6B00] hover:bg-[#e65a00] disabled:opacity-40 text-white text-xs font-bold px-4 py-2 rounded-full"
            data-testid="add-city-btn"
          >
            <Plus size={12} /> Add
          </button>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="local-cities-list">
          {(cfg.local_agent_cities || []).map((c) => (
            <span
              key={c}
              className="inline-flex items-center gap-1 border-2 border-zinc-200 bg-zinc-50 rounded-full pl-3 pr-1 py-1 text-xs font-bold text-zinc-700"
              data-testid={`city-chip-${c.replace(/\s/g, "-")}`}
            >
              {c}
              <button
                type="button"
                onClick={() => removeCity(c)}
                className="ml-1 w-5 h-5 inline-flex items-center justify-center rounded-full hover:bg-red-100 hover:text-red-600 text-zinc-400"
                aria-label={`Remove ${c}`}
                data-testid={`remove-city-${c.replace(/\s/g, "-")}`}
              >
                <X size={11} />
              </button>
            </span>
          ))}
          {(cfg.local_agent_cities || []).length === 0 && (
            <span className="text-xs text-zinc-500">No cities added yet — customers won't see the local-agent option.</span>
          )}
        </div>
      </section>
    </div>
  );
}

function ToggleRow({ icon: Icon, title, desc, checked, onToggle, testid }) {
  return (
    <div className={`flex items-start gap-3 p-3 rounded-xl border-2 transition-colors ${checked ? "border-emerald-300 bg-emerald-50/40" : "border-zinc-200 bg-zinc-50"}`}>
      <div className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${checked ? "bg-emerald-500 text-white" : "bg-zinc-200 text-zinc-500"}`}>
        <Icon size={18} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold text-zinc-900">{title}</div>
        <div className="text-[11px] text-zinc-500 mt-0.5 leading-snug">{desc}</div>
      </div>
      <button
        type="button"
        onClick={onToggle}
        className={`w-11 h-6 rounded-full relative transition-colors ${checked ? "bg-emerald-500" : "bg-zinc-300"}`}
        data-testid={testid}
        aria-pressed={checked}
      >
        <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all shadow ${checked ? "left-[22px]" : "left-0.5"}`} />
      </button>
    </div>
  );
}
